import { useEffect, useState } from 'react';

/**
 * Breakpoints, in px. These must stay identical to `--breakpoint-*` in
 * styles/theme.css (and to MUI's, while MUI is still rendering) — a component
 * that decides in JS what a CSS media query decides elsewhere has to agree with
 * it, or a table sheds columns at a different width than the sidebar collapses.
 */
export const BREAKPOINTS = {
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

const getWidth = () => (typeof window === 'undefined' ? BREAKPOINTS.lg : window.innerWidth);

/**
 * Current viewport width, throttled to one update per animation frame.
 *
 * One resize listener for the whole table rather than a `matchMedia` per
 * breakpoint, because callers ask "is it at least X" for several X at once.
 */
export function useViewportWidth(): number {
  const [width, setWidth] = useState(getWidth);

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(window.innerWidth));
    };
    window.addEventListener('resize', onResize);
    // Width can differ from the initial render if a scrollbar appeared.
    onResize();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return width;
}

/** True when the viewport is at least as wide as `bp`. */
export function useAtLeast(bp: Breakpoint): boolean {
  return useViewportWidth() >= BREAKPOINTS[bp];
}
